import React, { useEffect, useRef, useState } from 'react';
import QueryEditor from '../../../code/components/query-editor/QueryEditor';
import HdDuration from '../../../date-time/components/duration/HdDuration';
import { HdButton, HdIcon, HdTooltip } from '../../../components';
import HdIndeterminateProgressBar from '../../../progress-indicators/components/indeterminate-progress-bar/HdIndeterminateProgressBar';
import {
  TRACKER_ACTIVATION_SYNC_HISTORY_FAILED_RECORDS_DIALOG_DOWNLOAD_CLICK,
  TRACKER_ACTIVATION_SYNC_HISTORY_FAILED_RECORDS_DIALOG_QUERY_CLICK,
} from '../tracking';
import { saveAs } from 'file-saver';
import { getQueryResultCSV } from '../../../warehouse-management/workbench/utils/workbench';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import useKeyboardShortcutListener from '../../../a11y/shortcuts/hooks/useKeyboardShortcutListener';
import HdShortcut from '../../../a11y/shortcuts/components/shortcut/HdShortcut';
import { useQueryExecutioner } from '../../../warehouse-management/workbench/hooks/useQueryExecutioner';
import { ActivationQueryExecutionerAPI } from './ActivationQueryExecutionerAPI';
import HdPane from '../../../banner/components/hd-pane/HdPane';
import { HdCoordinatedScrollAnchor } from '../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import QueryOutputTable from '../../../warehouse-management/query-output-table/components/query-output-table/QueryOutputTable';
import { ScrollToViewportOnLoad } from '../../../a11y/scroll/components/scroll-to-viewport-on-load/ScrollToViewportOnLoad';
import styles from './styles.module.scss';

interface QueryFailedRecordsContainerProps {
  warehouseId: number;
  currentQuery: string;
  syncFailureType: any;
  editorRef: any;
}

function QueryFailedRecordsContainer({
 warehouseId,
 currentQuery,
 syncFailureType,
 editorRef
}: QueryFailedRecordsContainerProps) {
  const [queryRunOutput, setQueryRunOutput] = useState<any>();
  const [query, setQuery] = useState(currentQuery);
  const [queryExecutionResponse, setQueryExecutionResponse] = useState<any>(null);

  const coordinatedScrollAnchorRef = useRef(null);

  const { eventTrack } = useAnalyticsTracker();

  const stopQueryExecution$ = (taskId: any) => {
    return ActivationQueryExecutionerAPI.stopQueryExecution(taskId);
  };

  const getQueryExecutionTaskId$ = (sourceId: any) => {
    return ActivationQueryExecutionerAPI.getQueryExecutionTaskId(query, sourceId);
  };

  const getQueryExecutionTaskResponse$ = (taskId) => {
    return ActivationQueryExecutionerAPI.getQueryExecutionTaskResponse(taskId);
  };

  const {
    execute,
    stop,
    queryRunErrorMessage,
    runningQuery,
    lastRunActionTime,
    hasMoreResults,
  } = useQueryExecutioner({
    sourceId: warehouseId,
    query,
    queryExecutionResponse,
    onQueryExecutionResponseChange: setQueryExecutionResponse,
    stopQueryExecution$,
    getQueryExecutionTaskId$,
    getQueryExecutionTaskResponse$,
  });

  useEffect(() => {
    setQueryRunOutput(queryExecutionResponse?.data?.output);
  }, [queryExecutionResponse?.data?.output]);

  const runQueryClick = (usingShortcut = false) => {
    if (!currentQuery) {
      return;
    }

    eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_FAILED_RECORDS_DIALOG_QUERY_CLICK,
      properties: {
        warehouseId,
        usingShortcut,
        query: currentQuery,
        failureType: syncFailureType.value
      }
    });

    runningQuery ? stop() : execute();
  };

  const downloadQueryResult = () => {
    eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_FAILED_RECORDS_DIALOG_DOWNLOAD_CLICK,
      properties: {
        warehouseId: warehouseId,
        query: currentQuery,
        failureType: syncFailureType.value,
      },
    });

    const csvData = getQueryResultCSV(queryRunOutput);

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'query_result.csv');
  };

  const runQueryShortcutCommand = 'model.runSourceQuery';

  useKeyboardShortcutListener(runQueryClick, runQueryShortcutCommand, {
    priority: 0,
    terminal: 'match'
  });

  const dataIdGenerator = getDataIdGenerator('activation-query-editor-dialog');

  return (
    <div className='px-6 pb-6 overflow-auto'>
      <div className='box w-100'>
        <div className='box__header'>
          <div className='box__title'>
            SQL Query
          </div>
        </div>

        <div className='box__body'>
          <QueryEditor
            dataId={dataIdGenerator('query-editor')}
            value={query}
            onChange={setQuery}
            focusEditorOnLoad
            getCodeMirror={(ed) => {
              if (editorRef) {
                editorRef.current = ed;
              }
            }}
          />

          <div className=' center-flex-row justify-end mt-5'>
            {lastRunActionTime ? (
              <span className=' mr-4'>
                Run Time:
                <span className='text-medium ml-1'>
                  <HdDuration duration={lastRunActionTime} options={{ useAbbr: true }} />
                </span>
             </span>
            ) : null}

            <HdTooltip title={<HdShortcut command={runQueryShortcutCommand} />}>
              <HdButton
                dataId=''
                type='button'
                hdButton
                variation='outline'
                icon={runningQuery ? 'stop' : 'play'}
                onClick={runQueryClick}
                tooltip='runQueryTooltip'
                disabled={!query}
              >
                {runningQuery ? 'Stop' : 'Run'} Query
              </HdButton>
            </HdTooltip>
          </div>
        </div>
      </div>

      {runningQuery ? (
        <ScrollToViewportOnLoad>
          <HdIndeterminateProgressBar color='grey' className={styles.queryRunOutputLoading}>
            Hevo is running your query
          </HdIndeterminateProgressBar>
        </ScrollToViewportOnLoad>
      ) : null}

      {!!queryRunErrorMessage ? (
        <HdPane
          dataId={dataIdGenerator('query-run-error-message')}
          variant='error-faded'
          className='surface-box mt-5'
          icon='error-filled'
        >
          <div className='text-medium'>
            {queryRunErrorMessage}
          </div>
        </HdPane>
      ) : null}

      {queryRunOutput && !runningQuery ? (
        <div className='query-run-output mt-3 w-100'>
          <HdCoordinatedScrollAnchor elRef={coordinatedScrollAnchorRef.current}>
            <div
              className='box box--table flex-col'
              ref={coordinatedScrollAnchorRef}
            >
              <div className='box__header'>
                <div className='box__title'>
                  Results

                  {hasMoreResults ? (
                    <HdTooltip
                      title={`Showing first ${queryRunOutput?.rows?.length} rows for this warehouse`}
                      placement='right'
                    >
                      <HdIcon name='info' />
                    </HdTooltip>
                  ) : null}
                </div>

                <div className='box-header-actions'>
                  <HdButton
                    dataId=''
                    icon='download'
                    variation='outline'
                    size='sm'
                    onClick={() => downloadQueryResult()}
                  >
                    Download
                  </HdButton>
                </div>
              </div>

              <div className='box__body scroll-x flex-1'>
                <QueryOutputTable output={queryRunOutput} />
              </div>
            </div>
          </HdCoordinatedScrollAnchor>
        </div>
      ) : null}
    </div>
  );
}

export default QueryFailedRecordsContainer;

